import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import DatePicker, { CalendarContainer, registerLocale } from "react-datepicker";
import he from "date-fns/locale/he";
import "react-datepicker/dist/react-datepicker.css";
import { getLocalIsoDate, normalizeEventDate } from "../../Utils/eventDetails";
import "./EventDatePicker.css";

registerLocale("he", he);

const PHONE_QUERY = "(max-width: 600px)";
const PORTAL_ID = "event-date-picker-portal";

const toSelectedDate = (value) => {
  const normalized = normalizeEventDate(value);
  if (!normalized) return null;
  const selected = new Date(`${normalized}T12:00:00`);
  return Number.isNaN(selected.getTime()) ? null : selected;
};

const useIsPhone = () => {
  const [isPhone, setIsPhone] = useState(() => (
    typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(PHONE_QUERY).matches
  ));
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(PHONE_QUERY);
    const update = () => setIsPhone(query.matches);
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);
  return isPhone;
};

// Tap-only field: the calendar opens, the phone keyboard never does.
const TapOnlyInput = forwardRef(({ value, onClick, onFocus, onBlur, onKeyDown, id, className, placeholder, disabled, ...rest }, ref) => (
  <input
    ref={ref}
    id={id}
    type="text"
    className={className}
    value={value}
    placeholder={placeholder}
    disabled={disabled}
    readOnly
    inputMode="none"
    autoComplete="off"
    aria-label={rest["aria-label"]}
    aria-haspopup="dialog"
    onClick={onClick}
    onFocus={onFocus}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
  />
));
TapOnlyInput.displayName = "TapOnlyInput";

const EventDatePicker = ({ value, onChange, disabled = false, id = "event-date", placeholderText = "dd/mm/yyyy", ariaLabel }) => {
  const isPhone = useIsPhone();
  const pickerRef = useRef(null);

  // Phone: large centered Hebrew calendar with a title and a cancel button.
  const PhoneSheet = useMemo(() => ({ className, children }) => (
    <div className="event-date-sheet" dir="rtl">
      <div className="event-date-sheet__title">בחירת תאריך האירוע</div>
      <CalendarContainer className={className}>{children}</CalendarContainer>
      <button type="button" className="event-date-sheet__cancel" onClick={() => pickerRef.current?.setOpen(false)}>
        ביטול
      </button>
    </div>
  ), []);

  // Desktop dropdown. Many screens put the field inside a <label>; stopping the click's default
  // keeps the label from re-opening the calendar right after a day is picked.
  const DesktopContainer = useMemo(() => ({ className, children }) => (
    <div className="event-date-dropdown" onClick={(event) => event.preventDefault()}>
      <CalendarContainer className={className}>{children}</CalendarContainer>
    </div>
  ), []);

  return (
    <DatePicker
      ref={pickerRef}
      id={id}
      selected={toSelectedDate(value)}
      onChange={(date) => onChange(date ? getLocalIsoDate(date) : "")}
      disabled={disabled}
      locale="he"
      dateFormat="dd/MM/yyyy"
      minDate={new Date()}
      placeholderText={placeholderText}
      customInput={<TapOnlyInput aria-label={ariaLabel} />}
      className="form-control datetime-picker-input event-date-picker-input text-center"
      wrapperClassName="event-date-picker-wrapper"
      calendarClassName={isPhone ? "event-date-sheet__calendar" : undefined}
      calendarStartDay={0}
      withPortal={isPhone}
      portalId={isPhone ? PORTAL_ID : undefined}
      calendarContainer={isPhone ? PhoneSheet : DesktopContainer}
      autoComplete="off"
    />
  );
};

export default EventDatePicker;
